import React, { useEffect, useCallback, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import catchOnly from 'utils/catchOnly';

import FmcForm from 'components/FmcForm';
import OtpVerificationView from './OtpVerificationView';

import useOTPSender, { OTPSenderError } from 'hooks/requests/otpSender';
import useTimeout from 'hooks/useTimeout';

import { selectAttempts, selectExpireTime } from 'state/otpCode';
import { selectCreditApplication } from 'state/creditApplication';
import useQueryParameters from 'hooks/useQueryParameters';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import useWorkflow, { Step } from 'hooks/useWorkflow';
import useTranslate from 'hooks/useTranslate';
import useOTPVerification, {
  OTPVerificationError,
} from 'hooks/requests/otpVerification';
import { NonClientError } from 'hooks/requests/fetchUtils';
import useUnifiedConsumerSearch, {
  UnifiedConsumerSearchError,
} from 'hooks/requests/unifiedConsumerSearch';
import { isOtpCode } from 'utils/validations';
import { selectTenant } from 'state/tenant';

export type FormValues = {
  otpCode: string;
};

const initialValues: FormValues = {
  otpCode: '',
};

const UnifiedOtpVerification: React.FC = () => {
  const creditApplication = useSelector(selectCreditApplication);
  const otpAttempts = useSelector(selectAttempts);
  const otpExpireTime = useSelector(selectExpireTime);
  const { push } = useNavigationHelpers();
  const { pathAfter } = useWorkflow();
  const t = useTranslate();
  const [verifyOTPCode] = useOTPVerification();
  const [sendOTP] = useOTPSender();
  const query = useQueryParameters();
  const { enqueueSnackbar } = useSnackbar();
  const [unifiedConsumerSearch] = useUnifiedConsumerSearch();
  const {
    enabledFeatures: { requireAdditionalVerification },
  } = useSelector(selectTenant);

  const schema = Yup.object().shape({
    otpCode: Yup.string()
      .label(t('formLabel.otpCode'))
      .concat(isOtpCode)
      .required(),
  });

  const tooManyAttempts = useMemo(() => otpAttempts >= 3, [otpAttempts]);
  const attemptsRemaining = useMemo(() => {
    if (otpAttempts === 0) {
      return null;
    } else if (otpAttempts >= 3) {
      return t('unified.consumerSearch.otpVerification.outOfAttempts');
    } else {
      return t('unified.consumerSearch.otpVerification.attemptsLeft', {
        count: 3 - otpAttempts,
      });
    }
  }, [otpAttempts, t]);

  const [setTimeout] = useTimeout();
  const [timedOut, setTimedOut] = useState<boolean>(false);

  useEffect(() => {
    if (otpExpireTime && otpExpireTime > Date.now()) {
      setTimedOut(false);
      setTimeout(() => setTimedOut(true), otpExpireTime - Date.now());
    } else {
      // TODO when otpExpireTime state works properly, the next line should setTimedOut(true)
      setTimedOut(false);
    }
  }, [otpExpireTime, setTimeout, setTimedOut]);

  const onOtpSuccessful = useCallback(
    async (otpCode: string) => {
      const params = {
        mobilePhone: creditApplication.mobilePhone || undefined,
        emailAddress: creditApplication.emailAddress || undefined,
        storeNumber: creditApplication.storeNumber || undefined,
        associateId: creditApplication.associateId || undefined,
        carrier: query.carrier || undefined,
        keyword: query.keyword || undefined,
        shortCode: query.shortCode || undefined,
        otpCode: otpCode || undefined,
      };

      await unifiedConsumerSearch(params);
      console.log(params); // here to satisfy the linter; remove when hook is ready.

      push(pathAfter(Step.UNIFIED_CONSUMER_SEARCH));
    },
    [
      creditApplication,
      push,
      pathAfter,
      query.carrier,
      query.keyword,
      query.shortCode,
      unifiedConsumerSearch,
    ],
  );

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await verifyOTPCode({ otpCode: values.otpCode });
        await onOtpSuccessful(values.otpCode);
      } catch (e) {
        if (
          catchOnly(
            e,
            OTPVerificationError,
            NonClientError,
            UnifiedConsumerSearchError,
          )
        ) {
          if (
            e instanceof NonClientError ||
            e instanceof UnifiedConsumerSearchError
          ) {
            if (
              requireAdditionalVerification &&
              requireAdditionalVerification.requirePrefill
            ) {
              push('/require-additional-verification');
            } else {
              push(pathAfter(Step.UNIFIED_CONSUMER_SEARCH));
            }
          }
        }
      }
    },
    [
      verifyOTPCode,
      onOtpSuccessful,
      push,
      pathAfter,
      requireAdditionalVerification,
    ],
  );

  const resendSms = useCallback(async () => {
    if (creditApplication.mobilePhone === null) {
      throw new Error('Mobile phone must be present');
    }

    try {
      await sendOTP({ phone: creditApplication.mobilePhone });
      enqueueSnackbar(
        t('unified.consumerSearch.otpVerification.resendSmsSuccess'),
        {
          variant: 'success',
          persist: true,
        },
      );
    } catch (e) {
      if (catchOnly(e, NonClientError, OTPSenderError)) {
        enqueueSnackbar(
          t('unified.consumerSearch.otpVerification.resendSmsError'),
          {
            variant: 'error',
            persist: true,
          },
        );
      }
    }
  }, [t, sendOTP, enqueueSnackbar, creditApplication]);

  const mobilePhone = creditApplication.mobilePhone;

  return (
    <FmcForm
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {formProps => (
        <OtpVerificationView
          {...formProps}
          mobilePhone={mobilePhone || ''}
          resendSms={resendSms}
          attemptsRemaining={attemptsRemaining}
          tooManyAttempts={tooManyAttempts}
          timedOut={timedOut}
        />
      )}
    </FmcForm>
  );
};

export default UnifiedOtpVerification;
